import {
  AnalyticsStore,
  CreativeStore,
  FormatStateStore,
  MraidStore,
  UITimerStore,
  TimerStore,
  CountdownStore
} from 'common/stores';
import { TIMERS } from 'common/stores/constants/timers.constant';
import {
  AutoCloseHandler,
  ImpressionHandler,
  UserCloseHandler,
  UserDragHandler,
  UserOptoutHandler,
  UserExpandHandler,
  UserScrollHandler,
  MraidOnAdLoadedCallHandler,
  CreativeLoadedTrackHandler,
  UserClickHandler,
  Mrc50TrackHandler,
  Mrc100TrackHandler,
  EdgeToEdgeDectectionIssueHandler
} from 'common/stores/handlers';

import { makeAutoObservable } from 'mobx';
import * as React from 'react';
import { createPreloadingStore } from 'common/stores/preloading';
import { IPreloadingStore } from 'common/stores/preloading/IPreloadingStore';
import eventBus from 'common/event-handling/event-bus.model';
import { ImpressionSentEvent } from 'common/event-handling/events';
import { getUserClickHandler } from './UserClickHandlerFactory';
import { DisplayConfigType } from '../config';
import { getMraidStore } from './mraid-store-factory';
import { getFormatStateStore } from './format-state-store-factory';
import { TimerStoreBuilder } from './timer-store-builder';
import { getUserCloseHandler } from './user-close-handler-factory';

import { MraidUserClickHandler } from './handlers/MraidUserClickHandler';
import { FooterRedirectResizeHandler } from './handlers/FooterRedirectResizeHandler';
import { getMraidUnloadCallHandler } from './mraidUnloadCallHandlerFactory';
import { isWeb } from 'common/js/environment-utils';
import { getMwebSupplyEventsHandlers } from 'common/stores/handlers/mweb-supply-events-handlers/mweb-supply-events-handlers';
import { isSupplyMonitoringEventsEnabled } from 'common/js/supply-monitoring-events-enabling';

interface ContextProps {
  config: any;
  translate: any;
  context: AdContext;
}

type IBackgroundHandler = any;
interface RootStoreProps {
  formatName: FormatName;
  formatOptions: FormatOptions;
  formatContext: ContextProps;
  isMraidPolyfilled?: boolean;
}
export class RootStore {
  private static readonly OPEN_AUCTION_CAMPAIGN_FAMILY: string = 'marketplace';

  formatName: FormatName;
  adContext: AdContext;
  formatConfig: DisplayConfigType;
  formatOptions: FormatOptions;

  analyticsStore: AnalyticsStore;
  mraidStore: MraidStore;
  preloadingStore: IPreloadingStore;
  uiTimerStore: UITimerStore;
  countdownStore?: CountdownStore;
  creativeStore: CreativeStore;

  autoCloseHandler?: AutoCloseHandler;
  userCloseHandler: UserCloseHandler;
  userOptoutHandler: UserOptoutHandler;
  userClickHandler: UserClickHandler;
  userExpandHandler: UserExpandHandler;
  userDragHandler: UserDragHandler;

  formatStateStore: FormatStateStore;

  isInAppOpenAuction: boolean = false;
  injectMRAID: boolean = true;

  private backgroundHandlers: IBackgroundHandler[] = [];

  constructor({ formatName, formatContext, formatOptions }: RootStoreProps) {
    // Formats configuration
    this.formatName = formatName;
    this.adContext = formatContext.context;
    this.formatConfig = formatContext.config;
    this.formatOptions = formatOptions;

    const hasAutoClose: boolean =
      this.formatConfig?.closeBtn?.autoCloseAfter !== 0 &&
      typeof this.formatConfig?.closeBtn?.autoCloseAfter !== 'undefined' &&
      !window.isOgyPreview;

    const hasCloseBtn: boolean =
      typeof this.formatConfig?.closeBtn?.displayAfter !== 'undefined';

    this.formatStateStore = getFormatStateStore(
      this.adContext,
      this.formatConfig
    );

    // Default Stores
    this.uiTimerStore = new UITimerStore();

    this.mraidStore = getMraidStore({
      uiTimerStore: this.uiTimerStore,
      formatName: this.formatName,
      formatOptions: this.formatOptions,
      formatConfig: this.formatConfig,
      adContext: this.adContext,
      formatStateStore: this.formatStateStore,
      redirectClickHandler: (url: string, isRedirected: boolean, mraidOpen) => {
        (this.userClickHandler as MraidUserClickHandler).handleMraid(
          url,
          isRedirected,
          mraidOpen
        );
      }
    });

    const useCloseDownButton =
      this.formatConfig.closeBtn?.legacyUseCountdownCloseButton ||
      this.formatConfig.closeBtn?.useCountdownCloseButton;

    this.countdownStore = new CountdownStore({
      mraidStore: this.mraidStore
    });

    const subscriptionId = eventBus.subscribe(ImpressionSentEvent, event => {
      new TimerStoreBuilder(
        this.mraidStore,
        this.adContext,
        this.formatConfig
      ).run(this.uiTimerStore);

      this.backgroundHandlers.push(
        new Mrc50TrackHandler({
          analyticsStore: this.analyticsStore,
          formatConfig: this.formatConfig,
          mraidStore: this.mraidStore
        }),
        new Mrc100TrackHandler({
          analyticsStore: this.analyticsStore,
          formatConfig: this.formatConfig,
          mraidStore: this.mraidStore
        })
      );

      if (hasCloseBtn && useCloseDownButton) {
        this.countdownStore?.bindTimer(
          this.uiTimerStore.timers.get(TIMERS.CLOSE_BUTTON_TIMER) as TimerStore
        );
      }

      eventBus.unsubscribe(subscriptionId);
    });

    this.analyticsStore = AnalyticsStore.initStore({
      uiTimerStore: this.uiTimerStore,
      adContext: this.adContext,
      formatConfig: this.formatConfig
    });

    const footerRedirectResizeHandler = FooterRedirectResizeHandler.create({
      adContext: this.adContext,
      mraidStore: this.mraidStore,
      formatConfig: this.formatConfig
    });

    this.creativeStore = new CreativeStore({
      formatContext: formatContext,
      removeListener:
        ['interstitial', 'in_article'].indexOf(
          this.adContext.adUnitType ?? ''
        ) === -1,
      onResizeCallback: params =>
        footerRedirectResizeHandler?.handleResize({
          boundingClientRect: params.boundingClientRect
        })
    });

    this.preloadingStore = createPreloadingStore(this.formatConfig);

    // Common Handlers Stores
    this.userCloseHandler = getUserCloseHandler({
      analyticsStore: this.analyticsStore,
      mraidStore: this.mraidStore,
      adContext: this.adContext,
      formatName: this.formatName,
      formatConfig: this.formatConfig
    });

    this.userClickHandler = getUserClickHandler({
      adContext: this.adContext,
      handlerProps: {
        analyticsStore: this.analyticsStore,
        mraidStore: this.mraidStore,
        uiTimerStore: this.uiTimerStore,
        formatConfig: this.formatConfig,
        formatOptions: this.formatOptions,
        formatContext: this.adContext,
        formatName: this.formatName
      },
      formatStateStore: this.formatStateStore
    });

    this.userOptoutHandler = new UserOptoutHandler({
      analyticsStore: this.analyticsStore,
      mraidStore: this.mraidStore,
      formatOptions: this.formatOptions
    });

    this.backgroundHandlers.push(
      new ImpressionHandler({
        mraidStore: this.mraidStore,
        preloadingStore: this.preloadingStore,
        analyticsStore: this.analyticsStore,
        formatOptions: this.formatOptions,
        formatName: this.formatName
      })
    );

    this.userExpandHandler = new UserExpandHandler({
      analyticsStore: this.analyticsStore,
      mraidStore: this.mraidStore
    });

    if (this.adContext.adUnitType === 'header_ad') {
      this.backgroundHandlers.push(
        new UserScrollHandler({
          analyticsStore: this.analyticsStore,
          mraidStore: this.mraidStore
        })
      );
    }

    if (
      this.adContext.adUnitType === 'overlay_thumbnail' &&
      this.adContext.creativeSource === 'image'
    ) {
      this.userDragHandler = new UserDragHandler({
        analyticsStore: this.analyticsStore,
        formatContext: this.adContext
      });
    }

    this.backgroundHandlers.push(
      new MraidOnAdLoadedCallHandler({
        formatStateStore: this.formatStateStore,
        mraidStore: this.mraidStore
      })
    );

    this.backgroundHandlers.push(
      getMraidUnloadCallHandler({
        formatStateStore: this.formatStateStore,
        mraidStore: this.mraidStore,
        analyticsStore: this.analyticsStore,
        userClickHandler: this.userClickHandler,
        adContext: this.adContext,
        formatConfig: this.formatConfig
      })
    );

    this.backgroundHandlers.push(
      new CreativeLoadedTrackHandler({
        formatStateStore: this.formatStateStore,
        mraidStore: this.mraidStore,
        analyticsStore: this.analyticsStore
      })
    );

    this.backgroundHandlers.push(
      new EdgeToEdgeDectectionIssueHandler({
        mraidStore: this.mraidStore
      })
    );

    if (isSupplyMonitoringEventsEnabled(this.formatConfig)) {
      this.backgroundHandlers.push(
        ...getMwebSupplyEventsHandlers(
          this.adContext,
          this.formatStateStore.state
        )
      );
    }

    // Specific Handlers Stores
    if (hasAutoClose) {
      this.autoCloseHandler = new AutoCloseHandler({
        uiTimerStore: this.uiTimerStore,
        analyticsStore: this.analyticsStore,
        mraidStore: this.mraidStore
      });
    }

    const isWebOpenAuction =
      this.adContext.family?.toLowerCase() ===
        RootStore.OPEN_AUCTION_CAMPAIGN_FAMILY && isWeb();
    const isActiveViewInjected =
      this.formatConfig.isActiveViewInjected ?? false;
    const isNativeRedirect = this.formatConfig.redirectType === 'native';
    this.isInAppOpenAuction =
      this.adContext.family?.toLowerCase() ===
        RootStore.OPEN_AUCTION_CAMPAIGN_FAMILY && !isWeb();

    this.injectMRAID =
      (!isWebOpenAuction || isNativeRedirect) && !isActiveViewInjected;

    makeAutoObservable(this, {
      autoCloseHandler: hasAutoClose
    });
  }
}

export const StoreContext = React.createContext<RootStore>({} as RootStore);
